import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  useMediaQuery,
  DialogContentText,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { leaveRequestsAPI, leaveEntitlementsAPI, leaveTypesAPI, employeesAPI } from '../api/api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';
import { useTheme } from '@mui/material/styles';
import { logEvent } from '../utils/logger';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';


// Tarihi backend formatına uygun şekilde normalize et
function formatForBackend(dateString) {
  if (!dateString) return '';
  // Sadece tarih ise saat ekle
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString + 'T09:00';
  // Eğer 'YYYY-MM-DD HH:MM(:SS)' formatı varsa
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})?$/.test(dateString)) {
    return dateString.replace(' ', 'T').slice(0,16);
  }
  // Eğer 'YYYY-MM-DDTHH:MM(:SS)' formatı varsa
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(dateString)) {
    return dateString.slice(0,16);
  }
  // Fallback: parse ile formatla
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function useCompanySettings() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    // Use the configured API instance
    const fetchSettings = async () => {
      try {
        const response = await fetch('/company-settings');
        const data = await response.json();
        setSettings(data);
      } catch (error) {
        setSettings(null);
      }
    };
    fetchSettings();
  }, []);
  return settings;
}

function LeaveRequests() {
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    status: 'Pending',
  });
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [requestIdToDelete, setRequestIdToDelete] = useState(null);
  const [statusTab, setStatusTab] = useState('Pending');
  const [entitlements, setEntitlements] = useState([]);
  const [remainingDays, setRemainingDays] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const settings = useCompanySettings();
  const isSmallScreen = useMediaQuery('(max-width: 600px)');

  // Yardımcı fonksiyon: dd-MM-yyyy formatlı tarih
  function formatDateTR(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Utility to ensure date format is 'YYYY-MM-DDTHH:MM' for editing
  function formatDateForInput(date) {
    if (!date) return '';
    // Handles both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:MM' and returns 'YYYY-MM-DDTHH:MM'
    if (date.length === 10) return date + 'T09:00';
    if (date.length === 16) return date;
    // Try to parse and format
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return d.toISOString().slice(0,16);
  }

  const columns = [
    { field: 'RequestID', headerName: 'ID', width: 90 },
    { field: 'FirstName', headerName: 'Adı', flex: 1, minWidth: 100 },
    { field: 'LastName', headerName: 'Soyadı', flex: 1, minWidth: 100 },
    { field: 'EmployeeID', headerName: 'Çalışan ID', flex: 1, minWidth: 130 },
    { field: 'LeaveType', headerName: 'İzin Türü', flex: 1, minWidth: 120 },
    { field: 'StartDate', headerName: 'Başlangıç Tarihi', flex: 1, minWidth: 120, valueGetter: (params) => formatDateTR(params.row.StartDate) },
    { field: 'EndDate', headerName: 'Bitiş Tarihi', flex: 1, minWidth: 120, valueGetter: (params) => formatDateTR(params.row.EndDate) },
    { field: 'LeaveRequestStatus', headerName: 'Durum', flex: 1, minWidth: 120, valueGetter: (params) => params.row.LeaveRequestStatus || params.row.status || params.row.Status },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: isSmallScreen ? 80 : 100,
      renderCell: (params) => (
        <div>
          <IconButton aria-label="düzenle" onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton aria-label="sil" onClick={() => handleDeleteConfirmation(params.row.RequestID)} color="error">
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ];


  useEffect(() => {
    fetchLeaveRequests();
    fetchEmployees();
    fetchEntitlements();
    fetchLeaveTypes();
  }, []);

  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const response = await leaveRequestsAPI.getAll();
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : data.leaveRequests || data.leave_requests || data.requests || data.data || [];
      setRequests(list);
      setStatusTab('Pending');
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setError('Yetkisiz erişim! Lütfen tekrar giriş yapın.');
      } else {
        setError('İzin istekleri alınırken bir hata oluştu.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await employeesAPI.getAll();
      // En düzgün ve uyumlu format: { employee_id, name, department, position, status }
      const data = response.data.map((emp) => ({
        employee_id: emp.EmployeeId || emp.EmployeeID || emp.employee_id || emp.id,
        name: (emp.FirstName || emp.first_name || '') + ' ' + (emp.LastName || emp.last_name || ''),
        department: emp.Department || emp.department || '',
        position: emp.Position || emp.position || '',
        status: emp.Status || emp.status || '',
      })).filter((emp) => {
        const statusMatch = emp.status.toLowerCase() === 'aktif' || emp.status.toLowerCase() === 'active';
        const hasValidId = emp.employee_id !== undefined && emp.employee_id !== null && emp.employee_id !== '';
        return statusMatch && hasValidId;
      });
      setEmployees(data);
    } catch (error) {
      setError('Çalışanlar alınırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntitlements = async () => {
    try {
      const response = await leaveEntitlementsAPI.getAll();
      setEntitlements(response.data);
    } catch (error) {
      console.error('Error fetching leave entitlements:', error);
      // Don't show error for 500 errors as they might be backend issues
      if (error.response?.status !== 500) {
        setError('İzin hakları alınırken bir hata oluştu.');
      }
      // Set empty array as fallback to prevent crashes
      setEntitlements([]);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveTypesAPI.getAll();
      setLeaveTypes(response.data);
    } catch (error) {
      console.error('Error fetching leave types:', error);
      // Don't show error for 500 errors as they might be backend issues
      if (error.response?.status !== 500) {
        setError('İzin türleri alınırken bir hata oluştu.');
      }
      // Set empty array as fallback to prevent crashes
      setLeaveTypes([
        { type: 'Yıllık İzin' },
        { type: 'Hastalık İzni' },
        { type: 'Mazeret İzni' },
        { type: 'Evlilik İzni' },
        { type: 'Doğum İzni' }
      ]);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      leave_type: '',
      start_date: '',
      end_date: '',
      status: 'Pending',
    });
    setEmployeeId('');
    setError(null);
    setEditMode(false);
    setSelectedRequestId(null);
  };

  const calculateRequestedDays = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;
    
    // Calculate the difference in milliseconds
    const diffTime = Math.abs(end - start);
    // Convert to days and round up (partial days count as full days)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId || !formData.leave_type || !formData.start_date || !formData.end_date) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      setError('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }
    try {
      // The datetime-local input returns a string in the format "YYYY-MM-DDTHH:MM"
      // We need to ensure it's properly formatted for the backend
      
      // Create a simple date string in YYYY-MM-DD format
      const startDateObj = new Date(formData.start_date);
      const endDateObj = new Date(formData.end_date);
      
      // Format as YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const payload = {
        employee_id: employeeId,
        leave_type: formData.leave_type,
        start_date: formatDate(startDateObj),
        end_date: formatDate(endDateObj),
        status: formData.status || 'Pending',
      };
      
      // Validate all fields are present
      for (const key of Object.keys(payload)) {
        if (!payload[key]) throw new Error(key + ' alanı boş veya geçersiz!');
      }
      
      await logEvent('leave_request_submit', 'İzin talebi gönderiliyor', payload);
      
      //console.log('Submitting leave request:', payload);
      
      if (editMode) {
        const res = await leaveRequestsAPI.update(selectedRequestId, payload);
        if (res.data?.error) throw new Error(res.data.error);
        await logEvent('leave_request_edit', 'İzin talebi düzenlendi', { ...payload, request_id: selectedRequestId });
      } else {
        const res = await leaveRequestsAPI.create(payload);
        if (res.data?.error) throw new Error(res.data.error);
        await logEvent('leave_request_create', 'Yeni izin talebi oluşturuldu', payload);
      }
      
      fetchLeaveRequests();
      handleClose();
      setSuccess(true);
      await logEvent('leave_request_success', 'İzin talebi başarıyla gönderildi', payload);
    } catch (error) {
      console.error('Error submitting leave request:', error);
      // Show more specific error message if available from the backend
      const detail = error?.response?.data?.error || error?.response?.data?.message || error?.message || '';
      setError(`İzin isteği gönderilirken bir hata oluştu: ${detail}`);
      await logEvent('leave_request_error', 'İzin isteği gönderilirken hata oluştu', {
        error: detail,
        payload: {
          employee_id: employeeId,
          leave_type: formData.leave_type,
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: formData.status || 'Pending',
        }
      });
    }
  };

  const handleEdit = (row) => {
    setEditMode(true);
    setSelectedRequestId(row.RequestID || row.ID || row.LeaveRequestID);
    setEmployeeId(row.EmployeeID);
    setFormData({
      leave_type: row.LeaveType,
      start_date: formatDateForInput(row.StartDate),
      end_date: formatDateForInput(row.EndDate),
      status: row.Status || row.LeaveRequestStatus || 'Pending',
    });
    handleOpen();
  };

  const handleDeleteConfirmation = (id) => {
    setRequestIdToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (!requestIdToDelete) {
        setError('Silinecek izin talebi bulunamadı.');
        return;
      }
      await leaveRequestsAPI.delete(requestIdToDelete);
      fetchLeaveRequests();
      setSuccess(true);
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      setError(`İzin isteği silinirken bir hata oluştu: ${errorMessage}`);
    } finally {
      setDeleteConfirmationOpen(false);
      setRequestIdToDelete(null);
    }
  };

  // EXCEL AKTARIMI
  const handleExportExcel = () => {
    if (!requests || requests.length === 0) {
      alert('Aktarılacak izin talebi bulunamadı!');
      return;
    }
    const excelRows = [
      ['ID', 'Adı', 'Soyadı', 'Çalışan ID', 'İzin Türü', 'Başlangıç Tarihi', 'Bitiş Tarihi', 'Durum']
    ];
    requests.forEach(req => {
      excelRows.push([
        req.LeaveRequestID,
        req.FirstName,
        req.LastName,
        req.EmployeeID,
        req.LeaveType,
        formatDateTR(req.StartDate),
        formatDateTR(req.EndDate),
        req.LeaveRequestStatus || req.Status
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'İzin Talepleri');
    XLSX.writeFile(wb, `izin_talepleri_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // Seçili tab'a göre filtrele
  const filteredRequests = requests.filter(r => {
    const st = r.LeaveRequestStatus || r.status || r.Status || r.leave_request_status;
    return st === statusTab;
  });

  const updateRemaining = (empId, type) => {
    if (!empId || !type) {
      setRemainingDays(null);
      return;
    }
    const entitlement = entitlements.find(e => 
      e.EmployeeID === empId && e.LeaveType === type
    );
    setRemainingDays(entitlement ? entitlement.RemainingDays : null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Bu sayfada izin taleplerinizi yönetebilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <h2 style={{ margin: 0 }}>İzin Talepleri</h2>
      </div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={statusTab} onChange={(e, val) => setStatusTab(val)}>
          <Tab label="Beklemede" value="Pending" />
          <Tab label="Onaylandı" value="Approved" />
          <Tab label="Reddedildi" value="Rejected" />
        </Tabs>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Yeni İzin Talebi
          </Button>
          <Button variant="outlined" color="success" onClick={handleExportExcel}>
            İzin Taleplerini Excel'e Aktar
          </Button>
        </Box>
      </Box>
      <Paper elevation={3} sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#18191a' : '#fff', color: theme.palette.mode === 'dark' ? '#fff' : '#18191a', width: '100%', overflowX: 'auto' }}>
        <Box sx={{ height: 500, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredRequests}
              columns={columns}
              getRowId={(row) => row.RequestID}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#f5f5f5',
                  color: theme.palette.mode === 'dark' ? '#fff' : '#18191a',
                },
                '& .MuiDataGrid-cell': {
                  color: theme.palette.mode === 'dark' ? '#fff' : '#18191a',
                },
                bgcolor: 'transparent',
              }}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              loading={loading}
            />
          )}
        </Box>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'İzin Talebini Düzenle' : 'Yeni İzin Talebi'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Çalışan</InputLabel>
              <Select
                value={employeeId || ''}
                onChange={(e) => {
                  setEmployeeId(e.target.value);
                  updateRemaining(e.target.value, formData.leave_type);
                }}
                label="Çalışan"
              >
                {employees.filter(emp => emp.employee_id).map((employee, index) => (
                  <MenuItem key={`employee-${employee.employee_id || index}`} value={employee.employee_id}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>İzin Türü</InputLabel>
              <Select
                name="leave_type"
                value={formData.leave_type || ''}
                onChange={(e) => {
                  handleChange(e);
                  updateRemaining(employeeId, e.target.value);
                }}
                label="İzin Türü"
              >
                {leaveTypes.map((type, index) => (
                  <MenuItem key={`leave-type-${type.type || index}`} value={type.type}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {remainingDays !== null && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Kalan İzin Günü: {remainingDays} gün
              </Typography>
            )}

            <TextField
              fullWidth
              type="datetime-local"
              name="start_date"
              label="Başlangıç Tarihi"
              value={formData.start_date}
              onChange={handleChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              type="datetime-local"
              name="end_date"
              label="Bitiş Tarihi"
              value={formData.end_date}
              onChange={handleChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              error={new Date(formData.start_date) > new Date(formData.end_date)}
              helperText={new Date(formData.start_date) > new Date(formData.end_date) ? 'Bitiş tarihi başlangıç tarihinden önce olamaz' : ''}
            />

            {remainingDays !== null && calculateRequestedDays() > remainingDays && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Talep edilen gün sayısı ({calculateRequestedDays()} gün) kalan izin hakkından ({remainingDays} gün) fazla olamaz
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>İptal</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={
              !employeeId || 
              !formData.leave_type || 
              !formData.start_date || 
              !formData.end_date ||
              new Date(formData.start_date) > new Date(formData.end_date) ||
              (remainingDays !== null && calculateRequestedDays() > remainingDays)
            }
          >
            {editMode ? 'Güncelle' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"İzin İsteğini Sil"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            İzin isteğini silmek istediğinizden emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>İptal</Button>
          <Button onClick={handleDelete} autoFocus color='error'>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {editMode ? 'İzin isteği başarıyla güncellendi.' : 'İzin isteği başarıyla gönderildi.'}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LeaveRequests;
